/**
 * Map palette (docs/Visual-Style.md) and small colour helpers. Colours are 0xRRGGBB numbers so
 * they can go straight into vertex attributes, uniforms and canvas styles.
 *
 * `lighten`/`darken` mix towards the same paper-white / ink-black as the HUD's SVG counters
 * (game/format.ts) so map counters and HUD counters match exactly.
 */
import { FACTION_COLORS, NO_FACTION } from '@friction/engine';

export const PAL = {
  paper: 0xe8dfc8,
  ink: 0x2b2620,
  inkSoft: 0x4a4034,
  contour: 0x8c7a5a,
  river: 0x4f7d93,
  road: 0x7a5c3a,
  roadFill: 0xefe3c4,
  rail: 0x3b3530,
  brass: 0xb8955a,
  brassLight: 0xe2c68c,
  danger: 0xc2412f,
  frontInk: 0x5a1712,
  ok: 0x6f9a5b,
  amber: 0xc79a2e,
  table: 0x121316,
  cityGlow: 0xffc978,
  flash: 0xfff1c4,
  smoke: 0x6b6358,
} as const;

/** Linear mix of two 0xRRGGBB colours. */
export function mixColor(a: number, b: number, t: number): number {
  const ch = (s: number) => {
    const x = (a >> s) & 255;
    const y = (b >> s) & 255;
    return Math.round(x + (y - x) * t) << s;
  };
  return ch(16) | ch(8) | ch(0);
}

export const lighten = (c: number, t: number): number => mixColor(c, 0xf3ecdb, t);
export const darken = (c: number, t: number): number => mixColor(c, 0x14110d, t);

export const cssColor = (c: number, alpha = 1): string =>
  alpha >= 1
    ? `#${(c & 0xffffff).toString(16).padStart(6, '0')}`
    : `rgba(${(c >> 16) & 255},${(c >> 8) & 255},${c & 255},${alpha})`;

/** RGB floats 0..1 for uniforms. */
export function rgb01(
  c: number,
  out: Float32Array | number[] = [0, 0, 0],
  offset = 0,
): Float32Array | number[] {
  out[offset] = ((c >> 16) & 255) / 255;
  out[offset + 1] = ((c >> 8) & 255) / 255;
  out[offset + 2] = (c & 255) / 255;
  return out;
}

/**
 * Pack colour + alpha into the little-endian uint32 layout that a `unorm8x4` vertex attribute
 * reads as (r, g, b, a).
 */
export function packRgba(c: number, alpha: number): number {
  const a = Math.max(0, Math.min(255, Math.round(alpha * 255)));
  return ((a << 24) | ((c & 255) << 16) | (c & 0xff00) | ((c >> 16) & 255)) >>> 0;
}

/** Colour of a faction id, falling back to the default table for unknown ids. */
export function factionColor(colors: ReadonlyMap<number, number>, id: number): number {
  if (id === NO_FACTION) return PAL.paper;
  return colors.get(id) ?? FACTION_COLORS[id % FACTION_COLORS.length];
}
